import { Link, NavLink, useLocation } from "react-router-dom";

import { cn } from "./ui/cn";

/**
 * The NetXL app switcher.
 *
 * Pulse is the platform, and the Helpdesk, the CRM, Sales calls, Meetings and
 * Field Sales are the products inside it, so the outermost chrome belongs to
 * NetXL rather than to any of them: this rail stays put while everything to its
 * right is swapped out. It is deliberately the only surface painted in the brand colour
 * -- being the one constant on screen is what makes it read as "the platform"
 * instead of "part of whichever product is open".
 *
 * Apps that do not exist yet are listed and visibly disabled rather than
 * hidden. Showing where a product will live costs nothing and tells everyone the
 * shape of the thing being built; a rail that grows an item unannounced reads
 * as a bug the first time somebody notices it. The CRM sat here disabled for
 * exactly that reason until it was built.
 *
 * Lane is the exception: an outside app, kept below a rule and opened in its
 * own tab so that leaving Pulse is a visible thing rather than a surprise.
 */

/** Rail width in px. One tile plus breathing room, matching Lane's rail. */
export const APP_RAIL_WIDTH = 56;

interface AppEntry {
  id: string;
  label: string;
  /** Absent for apps that are not built yet. */
  to?: string;
  /**
   * The paths that belong to this app, matched as whole leading segments.
   *
   * Separate from `to` because where a tile *lands* is one page and what it
   * *owns* is several. Letting NavLink match on `to` lit the CRM tile on
   * /crm/companies and nowhere else in the CRM, and the Helpdesk tile on the
   * open view and nowhere else in the Helpdesk -- so moving to the dashboard
   * left the rail saying you were in no app at all.
   */
  owns: string[];
  icon: React.ReactNode;
}

const APPS: AppEntry[] = [
  {
    id: "helpdesk",
    label: "Helpdesk",
    /*
     * The root, not a view: the index route sends each agent to the view they
     * chose to land on (HelpdeskHome), so the rail and a fresh visit agree
     * without either holding its own copy of the answer.
     */
    to: "/",
    /*
     * The one app without a prefix of its own: it was Pulse before the others
     * existed, so its pages sit at the root. A page added under the Helpdesk
     * shell in App.tsx needs adding here too, or the rail goes dark on it.
     */
    owns: ["/v", "/dashboard", "/calls", "/canned", "/automations", "/audit"],
    icon: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m3 7.5 9 6 9-6" />
      </>
    ),
  },
  {
    id: "crm",
    label: "CRM",
    to: "/crm/companies",
    owns: ["/crm"],
    icon: (
      <>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20a7 7 0 0 1 14 0" />
      </>
    ),
  },
  {
    id: "sales",
    label: "Sales calls",
    to: "/sales",
    owns: ["/sales"],
    /*
     * A handset. Not the outgoing-call variant with an arrow: at 18px the arrow
     * is four pixels of diagonal that reads as noise, and the plain handset is
     * the one glyph everybody recognises without a label.
     */
    icon: (
      <path d="M6.5 3.5h3l1.5 4-2 1.5a12 12 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A17 17 0 0 1 4.5 5.7 2 2 0 0 1 6.5 3.5Z" />
    ),
  },
  {
    id: "meetings",
    label: "Meetings",
    to: "/meetings",
    owns: ["/meetings"],
    /*
     * A calendar. Its own app rather than a section inside Sales calls, and the
     * rail is where that distinction is made visible: the two are worked by
     * different halves of the desk in different places -- one at a phone, one
     * in a van -- and a field rep should land on their diary rather than on
     * somebody else's call pool.
     *
     * A calendar rather than a clock or a pin, because it is the one glyph that
     * survives 18px and still says "when" instead of "how long" or "where".
     */
    icon: (
      <>
        <rect x="3.5" y="5" width="17" height="15" rx="2" />
        <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
      </>
    ),
  },
  {
    id: "field",
    label: "Field Sales",
    to: "/field",
    owns: ["/field"],
    /*
     * A briefcase. Below Meetings because it is the field rep's front page over
     * the diary -- the two are used together, and the calendar tile above is
     * where its cards lead.
     *
     * A briefcase rather than a pin or a car: "out on the road" is what it is
     * for, and at 18px a pin reads as maps and a car as fleet.
     */
    icon: (
      <>
        <rect x="3.5" y="7.5" width="17" height="12" rx="2" />
        <path d="M9 7.5V5.5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 5.5v2M3.5 12.5h17" />
      </>
    ),
  },
];

/**
 * Lane, the task tracker.
 *
 * Below the products and behind a rule, because it is not one of them: it is a
 * separate app on a separate domain with its own login, and the rail would be
 * lying if Lane sat in the run with the Helpdesk and the CRM and then took the
 * whole window somewhere else. It earns a tile anyway -- the same people are in
 * it all day, tickets already spawn Lane tasks, and a bookmark is a worse
 * answer than a tile that is always in the same place.
 *
 * Hard-coded rather than read from config: the SPA is served as a static build
 * with no runtime configuration of any kind, and inventing one for a single
 * hostname would be the larger change. The API's own `LANE_WEB_URL` is what
 * builds the per-task links it hands back, and this must stay in step with it.
 */
const LANE_URL = "https://lane.netxl.com";

/*
 * Three lanes filling from the top and shortening left to right, which is
 * Lane's own mark redrawn on this rail's 24px grid -- round caps do the work
 * that the rounded rectangles do in the original. The heights carry the
 * meaning: it reads as a board draining into done, and it is the only tile
 * here that is not a picture of a noun.
 *
 * The only icon here that sets its own stroke. The rail's 1.75 is a line
 * weight, and these are not lines -- they are bars, a tenth of the canvas wide
 * in the original, and drawn at 1.75 they came out as three faint scratches
 * next to solid neighbours like the envelope. 3 is the weight that matches
 * their density; with the lanes 5.5 apart it still leaves a clear gap between
 * them. Caps add half the stroke at each end, so the drawn 5-to-19 lands as
 * 3.5-to-20.5 on screen -- the same extent as the calendar beside it.
 */
const LANE_ICON = <path strokeWidth={3} d="M6.5 5v14M12 5v10.8M17.5 5v7.6" />;

/**
 * Settings, at the foot of the rail rather than inside a product.
 *
 * Deliberately here and not in any product's sidebar. There is one `users`
 * table and one role behind all four -- a manager is a manager of Pulse, not of
 * the Helpdesk -- and a settings link inside each product would imply four sets
 * of permissions that never existed. The rail is the platform, so this is where
 * platform settings belong.
 */
/*
 * Sliders, not a cog. A cog drawn at 18px with a 1.75 stroke loses its teeth
 * and becomes a circle with spokes -- which is a sun, and reads as a brightness
 * control sitting directly above the theme toggle. Sliders survive the size.
 */
const SETTINGS_ICON = (
  <>
    <path d="M4 7h5m4 0h7M4 12h11m4 0h1M4 17h3m4 0h9" />
    <circle cx="11" cy="7" r="2" />
    <circle cx="17" cy="12" r="2" />
    <circle cx="9" cy="17" r="2" />
  </>
);

/**
 * Whether `pathname` is one of an app's pages.
 *
 * Whole segments, so "/v" owns "/v/mine" and not "/vendors", and "/dashboard"
 * -- the Helpdesk's -- does not claim "/crm/dashboard" or "/sales/dashboard".
 */
export function ownsPath(owns: string[], pathname: string): boolean {
  return owns.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

export function AppRail({ isAdmin }: { isAdmin: boolean }) {
  const { pathname } = useLocation();

  return (
    <nav
      aria-label="NetXL applications"
      style={{ width: APP_RAIL_WIDTH }}
      className="flex h-full shrink-0 flex-col items-center gap-1 bg-brand-600 py-2"
    >
      {/* Product mark. Not a link -- there is no "home" above the app level. */}
      <div
        className="mb-1 flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 text-sm font-bold text-white"
        title="NetXL"
        aria-hidden
      >
        N
      </div>

      {APPS.map((app) => {
        if (!app.to) {
          return (
            <span
              key={app.id}
              title={`${app.label} — coming soon`}
              aria-label={`${app.label}, coming soon`}
              aria-disabled
              className="flex h-9 w-9 cursor-not-allowed items-center justify-center rounded-lg text-white/35"
            >
              <AppIcon>{app.icon}</AppIcon>
            </span>
          );
        }

        const active = ownsPath(app.owns, pathname);

        return (
          <Link
            key={app.id}
            to={app.to}
            title={app.label}
            aria-label={app.label}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
              // White-on-brand for the active app: the strongest contrast
              // available without introducing a second accent colour.
              active
                ? "bg-white text-brand-700"
                : "text-white/75 hover:bg-white/15 hover:text-white",
            )}
          >
            <AppIcon>{app.icon}</AppIcon>
          </Link>
        );
      })}

      {/* The rule is the whole point of Lane's placement -- see LANE_URL. */}
      <span className="my-1 h-px w-5 bg-white/25" aria-hidden />

      <a
        href={LANE_URL}
        target="_blank"
        rel="noreferrer"
        title="Lane — opens in a new tab"
        aria-label="Lane, opens in a new tab"
        className="flex h-9 w-9 items-center justify-center rounded-lg text-white/75 transition-colors hover:bg-white/15 hover:text-white"
      >
        <AppIcon>{LANE_ICON}</AppIcon>
      </a>

      {/* Pushed to the foot, away from the products: it is not a fifth app, it
          is the thing that configures the other four. */}
      {isAdmin ? (
        <NavLink
          to="/settings"
          title="Settings"
          aria-label="Settings"
          className={({ isActive }) =>
            cn(
              "mt-auto flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
              isActive
                ? "bg-white text-brand-700"
                : "text-white/75 hover:bg-white/15 hover:text-white",
            )
          }
        >
          <AppIcon>{SETTINGS_ICON}</AppIcon>
        </NavLink>
      ) : null}
    </nav>
  );
}

function AppIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}
